import { Component, Input, ViewChild, ElementRef, OnDestroy, AfterViewInit } from '@angular/core';
import * as THREE from 'three';
import { GLTFLoader, GLTF } from 'three-stdlib';

@Component({
  selector: 'app-threejs-viewer',
  template: `
    <div class="viewer-container">
      <canvas #mainCanvas [style.visibility]="isLoading ? 'hidden' : 'visible'"></canvas>
      <div class="loading-overlay" *ngIf="isLoading">
        <div class="loading-content">
          <i class="pi pi-spin pi-spinner"></i>
          <span>Loading model...</span>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      position: relative;
    }

    .viewer-container {
      width: 100%;
      height: 100%;
      position: relative;
      background: #f8fafc;
      border-radius: 4px;
      overflow: hidden;
    }

    canvas {
      position: absolute;
      top: 0;
      left: 0;
      width: 100% !important;
      height: 100% !important;
    }

    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(248, 250, 252, 0.9);
      backdrop-filter: blur(4px);
    }

    .loading-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      color: #3b82f6;

      i {
        font-size: 2rem;
      }

      span {
        font-size: 0.875rem;
        font-weight: 500;
      }
    }
  `]
})
export class ThreeJsViewerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mainCanvas', { static: true }) mainCanvas: ElementRef<HTMLCanvasElement>;

  private readonly DEFAULT_MODEL = 'assets/models/14.glb';
  private _modelUrl: string;
  private isSceneInitialized = false;
  private pendingModelUrl: string | null = null;
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private mixer: THREE.AnimationMixer | null = null;
  private clock: THREE.Clock = new THREE.Clock();
  private animationFrameId: number;

  private _animationUrl: string;
  private pendingAnimationUrl: string | null = null;
  private currentModel: THREE.Object3D | null = null;

  private loadAttempts = 0;
  private maxAttempts = 2; // Original attempt + 1 fallback attempt

  private _currentAnimationUrl: string = '';

  public isLoading: boolean = true;

  private readonly MIN_LOADING_TIME = 500; // half a second in milliseconds
  private loadStartTime: number = 0;

  @Input() 
  set modelUrl(url: string) {
    this._modelUrl = url || this.DEFAULT_MODEL;
    if (this.isSceneInitialized && url) {
      this.loadModel(url);
    } else if (url) {
      this.pendingModelUrl = url;
    }
  }
  get modelUrl(): string {
    return this._modelUrl;
  }

  @Input()
  set animationUrl(url: string) {
    // Always attempt to load the animation even if URLs match
    this._animationUrl = url;
    if (this.isSceneInitialized && url && this.currentModel) {
      // Force animation reload even if it's the same URL
      this.loadAnimation(url);
    } else if (url) {
      this.pendingAnimationUrl = url;
    }
  }
  get animationUrl(): string {
    return this._animationUrl;
  }

  private loadAnimation(url: string): Promise<void> {
    if (!this.currentModel) {
      console.warn('Attempted to load animation without a model');
      return Promise.reject('No model loaded');
    }

    return new Promise((resolve, reject) => {
      // Stop any existing animation
      if (this.mixer) {
        this.mixer.stopAllAction();
      }

      const loader = new GLTFLoader();
      loader.load(
        url,
        (gltf) => {
          if (gltf.animations && gltf.animations.length > 0) {
            this.mixer = new THREE.AnimationMixer(this.currentModel);
            const action = this.mixer.clipAction(gltf.animations[0]);
            action.reset();
            action.play();
            action.loop = THREE.LoopRepeat;
            resolve();
          } else {
            resolve(); // Resolve even if no animations found
          }
        },
        undefined,
        (error) => {
          console.error('Error loading animation:', error);
          this.pendingAnimationUrl = url;
          reject(error);
        }
      );
    });
  }

  private async loadModel(url: string) {
    this.isLoading = true;
    this.loadStartTime = Date.now();

    if (this._modelUrl !== url) {
      this.loadAttempts = 0;
    }
    
    const objectsToRemove = this.scene.children.filter(
      child => !(child instanceof THREE.GridHelper || 
                child instanceof THREE.Light)
    );
    objectsToRemove.forEach(obj => this.scene.remove(obj));
    
    const currentAnimUrl = this._animationUrl;
    
    if (this.mixer) {
      this.mixer.stopAllAction();
      this.mixer = null;
    }
    this.currentModel = null;

    try {
      const gltf = await new Promise<GLTF>((resolve, reject) => {
        const loader = new GLTFLoader();
        loader.load(url, resolve, undefined, reject);
      });

      const modelScene = gltf.scene;
      this.currentModel = modelScene;
      this.scene.add(modelScene);
      this.adjustCameraToModel();

      // Handle animations based on context
      if (currentAnimUrl) {
        await this.loadAnimation(currentAnimUrl);
      } else if (gltf.animations && gltf.animations.length > 0) {
        this.mixer = new THREE.AnimationMixer(modelScene);
        const action = this.mixer.clipAction(gltf.animations[0]);
        action.play();
        action.paused = true;
        action.time = 0;
        this.mixer.update(0);
      }

      this.loadAttempts = 0;
      
      // Ensure minimum loading time
      const loadingTime = Date.now() - this.loadStartTime;
      if (loadingTime < this.MIN_LOADING_TIME) {
        await new Promise(resolve => setTimeout(resolve, this.MIN_LOADING_TIME - loadingTime));
      }
      
      this.isLoading = false;

    } catch (error) {
      console.error('Error loading model:', error);
      this.loadAttempts++;

      if (this.loadAttempts < this.maxAttempts && url !== this.DEFAULT_MODEL) {
        console.warn(`Failed to load model: ${url}, falling back to default model`);
        await this.loadModel(this.DEFAULT_MODEL);
      } else {
        console.error('Failed to load both requested and default models');
        
        // Ensure minimum loading time even on error
        const loadingTime = Date.now() - this.loadStartTime;
        if (loadingTime < this.MIN_LOADING_TIME) {
          await new Promise(resolve => setTimeout(resolve, this.MIN_LOADING_TIME - loadingTime));
        }
        
        this.isLoading = false;
      }
    }
  }

  ngAfterViewInit() {
    // Initialize Three.js after view is ready
    this.initializeThree();
    this.isSceneInitialized = true;

    // Load any pending model
    if (this.pendingModelUrl) {
      this.loadModel(this.pendingModelUrl);
      this.pendingModelUrl = null;
    }
  }

  ngOnDestroy() {
    // Clean up resources
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.dispose();
  }

  private initializeThree() {
    // Initialize renderer
    this.renderer = new THREE.WebGLRenderer({ 
      canvas: this.mainCanvas.nativeElement, 
      antialias: true,
      alpha: true 
    });
    
    // Get the actual size of the container
    const container = this.mainCanvas.nativeElement.parentElement;
    const width = container.clientWidth;
    const height = container.clientHeight;
    
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(width, height, false); // false to prevent setting canvas style
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // Initialize scene
    this.scene = new THREE.Scene();
    this.setupBackground();
    this.setupLighting();
    this.setupGrid();
    
    // Initialize camera
    this.camera = new THREE.PerspectiveCamera(
      50,
      this.mainCanvas.nativeElement.clientWidth / this.mainCanvas.nativeElement.clientHeight,
      0.1,
      1000
    );
    this.camera.position.set(0, 2, 5);
    this.camera.lookAt(0, 1, 0);

    // Start animation loop
    this.animate();

    // Handle window resize
    window.addEventListener('resize', this.onWindowResize.bind(this));
  }

  private setupBackground() {
    const vertexShader = `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = vec4(position, 1.0);
      }
    `;

    const fragmentShader = `
      varying vec2 vUv;
      void main() {
        vec3 topColor = vec3(0.8, 0.8, 0.8);
        vec3 bottomColor = vec3(0.65, 0.65, 0.65);
        vec3 color = mix(bottomColor, topColor, vUv.y);
        gl_FragColor = vec4(color, 1.0);
      }
    `;

    const gradientMaterial = new THREE.ShaderMaterial({
      uniforms: {},
      vertexShader,
      fragmentShader,
    });

    const gradientGeometry = new THREE.PlaneGeometry(2, 2);
    const gradientMesh = new THREE.Mesh(gradientGeometry, gradientMaterial);
    gradientMesh.material.depthWrite = false;
    gradientMesh.renderOrder = -1;

    const gradientScene = new THREE.Scene();
    const gradientCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    gradientScene.add(gradientMesh);

    // Store gradient scene and camera for use in animation
    (this as any).gradientScene = gradientScene;
    (this as any).gradientCamera = gradientCamera;
  }

  private setupLighting() {
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(ambientLight);
  
    const frontLight = new THREE.DirectionalLight(0xffffff, 3.0);
    frontLight.position.set(0, 2, 4);
    frontLight.castShadow = true;
    this.scene.add(frontLight);
  
    const topLight = new THREE.DirectionalLight(0xffffff, 0.8);
    topLight.position.set(0, 5, 0);
    this.scene.add(topLight);
  
    const leftFill = new THREE.DirectionalLight(0xffffff, 0.3);
    leftFill.position.set(-3, 1, 0);
    this.scene.add(leftFill);
    
    const rightFill = new THREE.DirectionalLight(0xffffff, 0.3);
    rightFill.position.set(3, 1, 0);
    this.scene.add(rightFill);
  }

  private setupGrid() {
    const gridHelper = new THREE.GridHelper(20, 20, 0x808080, 0xCCCCCC);
    gridHelper.position.y = 0;
    this.scene.add(gridHelper);
  }

  private animate = () => {
    this.animationFrameId = requestAnimationFrame(this.animate);
    
    const delta = this.clock.getDelta();
    if (this.mixer) {
      this.mixer.update(delta);
    }

    // Render gradient background first
    this.renderer.autoClear = false;
    this.renderer.clear();
    this.renderer.render((this as any).gradientScene, (this as any).gradientCamera);
    
    // Then render the main scene
    this.renderer.render(this.scene, this.camera);
  }

  private adjustCameraToModel() {
    const objects = this.scene.children.filter(
      child => !(child instanceof THREE.GridHelper || 
                child instanceof THREE.Light)
    );
    
    const group = new THREE.Group();
    objects.forEach(obj => group.add(obj.clone()));
    
    const bbox = new THREE.Box3().setFromObject(group);
    const center = bbox.getCenter(new THREE.Vector3());
    const size = bbox.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = this.camera.fov * (Math.PI / 180);
    let cameraZ = maxDim / (2 * Math.tan(fov / 2));

    cameraZ *= 1.3;

    const cameraPosition = new THREE.Vector3(
      center.x,
      center.y + maxDim * 0.2,
      cameraZ
    );
    this.camera.position.copy(cameraPosition);
    this.camera.lookAt(center);
    this.camera.updateProjectionMatrix();
    
    group.clear();
  }

  private onWindowResize() {
    if (!this.mainCanvas) return;
    
    const container = this.mainCanvas.nativeElement.parentElement;
    const width = container.clientWidth;
    const height = container.clientHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    
    this.renderer.setSize(width, height, false);
    this.adjustCameraToModel();
  }

  private dispose() {
    // Dispose of Three.js resources
    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        if (object.geometry) {
          object.geometry.dispose();
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => this.disposeMaterial(material));
          } else {
            this.disposeMaterial(object.material);
          }
        }
      }
    });

    this.renderer.dispose();
    
    // Remove event listener
    window.removeEventListener('resize', this.onWindowResize.bind(this));
  }

  private disposeMaterial(material: THREE.Material) {
    material.dispose();
    // Dispose of any textures
    Object.values(material).forEach(value => {
      if (value instanceof THREE.Texture) {
        value.dispose();
      }
    });
  }
}